import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from '@leadsnavi/ui-kit';
// import mixpanel from 'mixpanel-browser';
import { useMailEditorStore } from '@/stores/mailEditorStore';
import { editDraftEmailPost, sendEmailPost } from '@/services/swagger/easourcing/WriteService';
import type { MailEditorRef } from '@/components/mail-editor';
import { mergeHtmlContent } from './useMergeHtmlContent';
import { extractContentIdFromResponse, transformAttachments } from './useAutoSaveDraft';

// 转换附件格式函数已移除，暂时未使用

/**
 * 邮件编辑器操作 Hook
 * 封装 Save 和 Send 的通用逻辑
 *
 * 注意：根据技术方案，已删除草稿保存功能，发送前直接调用 sendEmailPost
 */
export function useMailEditorActions(editorRef: React.RefObject<MailEditorRef>, subject: string) {
  const { contactId } = useParams<{ contactId: string }>();
  const { setSubject, setContent, setAttachments, setEditorMode, setDialogVisible, clearAll, quotedContent, draftId } =
    useMailEditorStore();

  const [saveLoading, setSaveLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);

  /**
   * 重置编辑器（Save/Reset 按钮）
   * 清空编辑器和标题附件，还原成最初无编辑状态
   */
  const handleSave = useCallback(async () => {
    if (!editorRef.current) {
      return;
    }

    setSaveLoading(true);
    try {
      // 清空编辑器内容
      editorRef.current.setContent('');
      editorRef.current.setAttachments([]);

      // 清空 store 状态
      clearAll();
      setSubject('');

      toast.success('Editor reset successfully');
    } catch (err) {
      console.error('resetEditor', err);
      toast.error(err instanceof Error ? err.message : 'Failed to reset editor');
    } finally {
      setSaveLoading(false);
    }
  }, [editorRef, clearAll, setSubject]);

  /**
   * 发送邮件
   * 根据技术方案，删除草稿保存逻辑，直接发送
   */
  const handleSend = useCallback(
    async (onSuccess?: () => void) => {
      // 同步 subject 到 store
      setSubject(subject);

      if (!subject) {
        toast.error('Please enter the Subject');
        return;
      }

      if (!contactId) {
        toast.error('Contact ID is required');
        return;
      }

      if (!editorRef.current) {
        toast.error('Editor is not ready');
        return;
      }

      // mixpanel.track('contactsdetails_send_click');
      setSendLoading(true);

      try {
        // 获取编辑器内容和附件
        const editorContent = editorRef.current.getContent() || '';
        const currentContent = mergeHtmlContent(editorContent, quotedContent || '');
        const currentAttachments = editorRef.current.getAttachments() || [];

        // 同步到 store
        setContent(currentContent);
        setAttachments(currentAttachments);
        const contentRes = await editDraftEmailPost({
          contactId,
          contentId: draftId || '', // 新项目不使用草稿，contentId 为空
          subject,
          content: currentContent,
          attachments: transformAttachments(currentAttachments),
          manualSave: 1,
        });
        const contentId = extractContentIdFromResponse(contentRes);
        // 直接发送邮件（不再保存草稿）
        // 注意：sendEmailPost 需要 contentId，但根据技术方案，新项目不再使用草稿功能
        // 这里可能需要后端支持直接发送（不依赖 contentId），或者创建一个临时 contentId
        // 暂时使用空 contentId，如果后端不支持，需要调整
        await sendEmailPost(
          {
            contactId,
            contentId: contentId || draftId || undefined, // 新项目不使用草稿，contentId 为空
          },
          { skipGlobalErrorHandling: true }
        );

        toast.success('Email sent successfully');
        clearAll(); // 清空所有数据（包括 receiverEmail）
        setSubject('');
        setContent('');
        setAttachments([]);
        editorRef.current?.setContent('');
        editorRef.current?.setAttachments([]);
        setEditorMode('window');
        setDialogVisible(false);

        onSuccess?.();
      } catch (err) {
        console.error('sendMail', err);
        toast.error((err as unknown as { message?: string })?.message || 'Failed to send email');
        throw err;
      } finally {
        setSendLoading(false);
      }
    },
    [
      setSubject,
      subject,
      contactId,
      editorRef,
      quotedContent,
      setContent,
      setAttachments,
      draftId,
      clearAll,
      setEditorMode,
      setDialogVisible,
    ]
  );

  return {
    handleSave,
    handleSend,
    saveLoading,
    sendLoading,
  };
}
